import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Popconfirm,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { useState } from 'react';
import {
  ClientFeatures as ClientFeatureType,
  FeatureValue,
} from '../../types/features';
import {
  FEATURE_DISPLAY,
  FeatureDisplay,
  humanizeFeatureName,
  SYNC_FEATURE_FLAGS,
} from './feature-display';

interface ClientFeaturesProps {
  features: ClientFeatureType;
  toggleFeature: (feature: string) => void;
  enableFeatures: (features: string[]) => Promise<void>;
}

const ALPHA_BADGE_STYLE: React.CSSProperties = {
  display: 'inline-block',
  marginRight: 6,
  padding: '1px 8px',
  borderRadius: 8,
  background: '#fff0f6',
  color: '#c41d7f',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.3,
};

function FeatureCheckbox({
  name,
  value,
  display,
  onToggle,
}: {
  name: string;
  value: FeatureValue;
  display: FeatureDisplay;
  onToggle: () => void;
}) {
  return (
    <Checkbox checked={value === FeatureValue.ALL} onChange={onToggle}>
      {display.badge === 'Alpha' && (
        <span style={ALPHA_BADGE_STYLE}>Alpha</span>
      )}
      {display.label}{' '}
      <Tooltip title={display.tooltip}>
        <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
      </Tooltip>
      <Typography.Text style={{ marginLeft: 8, fontSize: 12 }} type="secondary">
        {name}
      </Typography.Text>
    </Checkbox>
  );
}

export function ClientFeatures({
  features,
  toggleFeature,
  enableFeatures,
}: ClientFeaturesProps) {
  const [showExperimental, setShowExperimental] = useState(false);

  const curatedKeys = Object.keys(FEATURE_DISPLAY).filter(
    key => key in features,
  );
  const experimentalKeys = Object.keys(features)
    .filter(key => !(key in FEATURE_DISPLAY))
    .sort();

  return (
    <div
      style={{ textAlign: 'left', backgroundColor: 'white', padding: '20px' }}
    >
      <Typography.Title level={2}>Client Features</Typography.Title>
      <Space direction="vertical">
        {curatedKeys.map(key => (
          <FeatureCheckbox
            display={FEATURE_DISPLAY[key]}
            key={key}
            name={key}
            onToggle={() => toggleFeature(key)}
            value={features[key]}
          />
        ))}
      </Space>

      <div style={{ marginTop: 24 }}>
        <Popconfirm
          onConfirm={() => enableFeatures(SYNC_FEATURE_FLAGS)}
          title={`Turn on all ${SYNC_FEATURE_FLAGS.length} sync flags for this client?`}
        >
          <Button type="primary">Enable sync flags</Button>
        </Popconfirm>
      </div>

      {experimentalKeys.length > 0 && (
        <>
          <div style={{ marginTop: 24, marginBottom: 12 }}>
            <Space>
              <Switch
                checked={showExperimental}
                onChange={setShowExperimental}
              />
              <Typography.Text>
                Show experimental flags ({experimentalKeys.length})
              </Typography.Text>
            </Space>
          </div>
          {showExperimental && (
            <Space direction="vertical">
              {experimentalKeys.map(key => (
                <FeatureCheckbox
                  display={{
                    label: humanizeFeatureName(key),
                    tooltip: `Experimental flag: ${key}. No description available — check the backend Features enum.`,
                  }}
                  key={key}
                  name={key}
                  onToggle={() => toggleFeature(key)}
                  value={features[key]}
                />
              ))}
            </Space>
          )}
        </>
      )}
    </div>
  );
}
